import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Select from 'react-select';

import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import cancelIcon from '../../../shared/assets/svg/cancel_icon.svg';
import {
  getAllBlocks,
  getUnitsByProjectId,
  getUnitsWithAdvancePaymentStages,
  getBlocksWithAdvancePaymentStages
} from '../../services/api/implementation/impl';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Loader from '../../../shared/components/ui/Loader';

const AddBulkPaymentStage = ({
  handleOnPaymentStage,
  onClose,
  isSubmitting,
  projectId,
  isIndependent = false
}) => {
  const userId = Cookies.get('userId');
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [isLoadingEligibleItems, setIsLoadingEligibleItems] = useState(false);

  // 1. Fetch all blocks or units based on isIndependent
  const { data: blocksDyna, error: blocksError } = useQuery({
    queryKey: isIndependent ? ['units', projectId] : ['blocks', projectId],
    queryFn: () => (isIndependent ? getUnitsByProjectId(projectId) : getAllBlocks(projectId)),
    enabled: !!projectId,
    onError: (error) => {
      console.error(`Error fetching ${isIndependent ? 'units' : 'blocks'}:`, error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          `Failed to fetch ${isIndependent ? 'units' : 'blocks'}`
      );
    }
  });

  // 2. For each block, fetch its payment stages to filter blocks with only advance payment

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Bulk Payment Stage Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    const fetchPaymentStages = async () => {
      if (!blocksDyna || blocksDyna.length === 0) {
        setAvailableBlocks([]);
        setIsLoadingEligibleItems(false);
        return;
      }

      setIsLoadingEligibleItems(true);
      const eligibleBlocks = [];

      try {
        const stages = isIndependent
          ? await getUnitsWithAdvancePaymentStages(projectId)
          : await getBlocksWithAdvancePaymentStages(projectId);
        // Check if block/unit only has one payment stage named "Advance"
        if (isIndependent) {
          for (const item of stages) {
            eligibleBlocks.push({
              value: item.id,
              label: item.unitName + ' (' + item.block.name + ')' || `Unit ${item.id}`
            });
          }
        } else {
          for (const item of stages) {
            eligibleBlocks.push({
              value: item.id,
              label: item.name || `Block ${item.id}`
            });
          }
        }

        eligibleBlocks.sort((a, b) => a.label.localeCompare(b.label));
        setAvailableBlocks(eligibleBlocks);
      } catch (error) {
        console.error(
          `Error fetching payment stages for ${isIndependent ? 'unit' : 'block'} :`,
          error
        );
      } finally {
        // ✅ sort once after loop

        setIsLoadingEligibleItems(false);
      }
    };

    fetchPaymentStages();
  }, [blocksDyna, isIndependent]);

  // 3. Setup react-hook-form
  const { register, handleSubmit, control, watch, setValue, reset } = useForm({
    defaultValues: {
      payment: []
    }
  });

  // 4. UseFieldArray for dynamic addition/removal of payment stages
  const {
    fields: paymentStageFields,
    append: appendPaymentStage,
    remove: removePaymentStage
  } = useFieldArray({
    control,
    name: 'payment'
  });

  // 5. Watch the fields to keep track of the total percentage
  const watchPaymentFields = watch('payment');

  useEffect(() => {
    const total = watchPaymentFields.reduce(
      (acc, curr) => acc + (parseFloat(curr.percentage) || 0),
      0
    );
    setTotalPercentage(total);
  }, [watchPaymentFields]);

  // 6. Handle percentage input changes
  const handlePercentageChange = (index, event) => {
    let value = parseFloat(event.target.value);

    if (isNaN(value)) {
      value = 0;
    }

    // Allow direct entry of values up to 90
    setValue(`payment.${index}.percentage`, value);

    // Calculate new total immediately
    const total = watchPaymentFields.reduce((acc, curr, idx) => {
      if (idx === index) {
        return acc + value;
      }
      return acc + (parseFloat(curr.percentage) || 0);
    }, 0);

    setTotalPercentage(total);

    // If total would exceed 90%, reset to maximum allowed
    if (total > 90) {
      const maxAllowed =
        90 -
        watchPaymentFields.reduce(
          (acc, curr, idx) => (idx === index ? acc : acc + (parseFloat(curr.percentage) || 0)),
          0
        );
      setValue(`payment.${index}.percentage`, maxAllowed);
      setTotalPercentage(90);
    }
  };

  // 7. Add a new payment stage
  const handleAddNewStages = () => {
    if (totalPercentage >= 90) {
      toast.error('Total percentage with Advance (10%) would exceed 100%');
      return;
    }

    appendPaymentStage({
      name: `Payment ${paymentStageFields.length + 1}`,
      percentage: ''
    });
  };

  // 8. Form submit
  const handleOnSubmit = (data) => {
    if (selectedBlocks.length === 0) {
      toast.error(`Please select at least one ${isIndependent ? 'unit' : 'block'}`);
      return;
    }

    if (totalPercentage < 90) {
      toast.error('Total percentage must be exactly 90% (plus 10% Advance to make 100%)');
      return;
    }

    if (data.payment.length === 0) {
      toast.error('Please add at least one payment stage');
      return;
    }

    if (data.payment.some((stage) => !stage.name.trim())) {
      toast.error('All payment stages must have names');
      return;
    }

    if (data.payment.some((stage) => !stage.percentage || parseFloat(stage.percentage) <= 0)) {
      toast.error('All payment stages must have valid percentages');
      return;
    }

    const payload = {
      ...(isIndependent
        ? { unit_ids: selectedBlocks.map((block) => block.value) }
        : { block_ids: selectedBlocks.map((block) => block.value) }),
      stages: data.payment.map((stage) => ({
        name: stage.name,
        percentage: stage.percentage.toString()
      }))
    };

    handleOnPaymentStage(payload);
  };

  return (
    <div className="mt-10">
      <Loader isVisible={isLoadingEligibleItems} />
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {isIndependent
            ? 'Select (only units with Advance payment stage)'
            : 'Select (only blocks with Advance payment stage)'}
        </label>
        <Select
          isMulti
          options={availableBlocks}
          value={selectedBlocks}
          onChange={setSelectedBlocks}
          className="basic-multi-select"
          classNamePrefix="select"
          isDisabled={isLoadingEligibleItems}
          isLoading={isLoadingEligibleItems}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 })
          }}
        />
      </div>

      <div className="max-h-[30rem] overflow-y-auto hide-scrollbar">
        <table className="w-full table-auto border rounded-lg">
          <thead className="bg-gray-200 !text-gray-600 rounded-t-xl">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Percentage</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-2">
                <input
                  type="text"
                  value="Advance"
                  disabled
                  className="border rounded p-2 w-full bg-gray-100"
                />
              </td>
              <td className="px-4 py-2">
                <input
                  type="number"
                  value="10"
                  disabled
                  className="border rounded p-2 w-full bg-gray-100"
                />
              </td>
              <td className="px-4"></td>
            </tr>
            {paymentStageFields?.map((field, index) => (
              <tr key={field.id}>
                <td className="px-4 py-2">
                  <FormInput
                    register={register}
                    register_key={`payment.${index}.name`}
                    type="text"
                    placeholder={`Payment ${index + 1}`}
                  />
                </td>
                <td className="px-4 py-2">
                  <input
                    {...register(`payment.${index}.percentage`)}
                    type="number"
                    placeholder="Percentage"
                    min="0"
                    max="100"
                    step="0.01"
                    onChange={(e) => handlePercentageChange(index, e)}
                    value={watchPaymentFields[index].percentage || ''}
                    className="border rounded p-2 w-full"
                  />
                </td>
                <td className="px-4">
                  <div className="ml-6" onClick={() => removePaymentStage(index)}>
                    <img className="cursor-pointer" src={cancelIcon} alt="cancel" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4">
        <p>Total Percentage (including 10% Advance): {(totalPercentage + 10).toFixed(2)}%</p>
        {totalPercentage !== 90 && (
          <p className="text-red-500">
            Total percentage must be exactly 90% (Advance payment is 10%)
          </p>
        )}
      </div>

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button displayType="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          displayType="secondary"
          onClick={handleAddNewStages}
          disabled={totalPercentage >= 90}>
          Add New Stage
        </Button>
        <Button onClick={handleSubmit(handleOnSubmit)} disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default AddBulkPaymentStage;
